import axios from 'axios'
import * as Constants from "../../consts/Constants";

import Api from "./api"
import { Session, Credential, Token } from "./models"
import sha256 from "js-sha256";

const AUTH_KEY = "auth";
const SESSION_KEY = "session";

class AuthService {

  constructor(baseUrl) {
    this.api = new Api(baseUrl);

    let auth = JSON.parse(localStorage.getItem(AUTH_KEY));
    if (auth != null) {
      localStorage.removeItem(AUTH_KEY);
      this.setSession(new Credential(auth.credentials), new Token(auth))
    } else {
      let session = JSON.parse(localStorage.getItem(SESSION_KEY));
      if (session != null) {
        this.setSession(new Credential(session.credential.email, session.credential.password), new Token(session.token))
      }
    }    
    this.configureAxios();
  }

  createSession(email, password, callback) {
    this.clearSession();

    let hashedPassword = sha256(password);
    
    this.api.createSession(email, hashedPassword, (response) => {
      if (response.result != null) {
        this.setSession(new Credential(email,hashedPassword), new Token(response.result));
        callback(response);
      } else {
        callback(response);
      }
    })

  }

  logout() {
    this.clearSession();
  }

  setSession(credential, token) {
    this.session = new Session(credential, token)
    localStorage.setItem(SESSION_KEY, JSON.stringify(this.session));
    this.api.client.defaults.headers.common["Authorization"] = "Bearer " + this.session.token.token;
    axios.defaults.headers.common["Authorization"] = "Bearer " + this.session.token.token;
  }

  clearSession() {
    this.session = null;
    localStorage.removeItem(SESSION_KEY);
    this.api.client.defaults.headers.common["Authorization"] = "";
    axios.defaults.headers.common["Authorization"] = "";
  }

  getUser() {
    return this.session.token.getUser();
  }

  hasSession() {
    return this.session != null;
  }

  setSessionListener(listener) {
    this.listener = listener;
  }

  configureAxios() {
    this.api.client.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response.data) {

          const isJsonBlob = (data) => data instanceof Blob && data.type === "application/json";
          const responseData = isJsonBlob(error.response.data) ? JSON.parse(await error.response.data.text()) : error.response.data

          if (responseData.descriptor === "Auth.InvalidToken") {
            originalRequest._retry = true;
            return this.api.createSession(this.session.credential.email, this.session.credential.password, (response) => {
              if (response.result != null) {
                this.setSession(this.session.credential, new Token(response.result));
                originalRequest.headers.Authorization = "Bearer " + this.session.token.token;
                return this.api.client(originalRequest);
              }
            });
          } else if (responseData.descriptor === "Auth.InvalidPassword") {
            this.clearSession();
            if (this.listener)
              this.listener.invalidPassword();
          }

          throw error;

        }
        
        
      }
    );

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response.data) {

          const isJsonBlob = (data) => data instanceof Blob && data.type === "application/json";
          const responseData = isJsonBlob(error.response.data) ? JSON.parse(await error.response.data.text()) : error.response.data

          if (responseData.descriptor === "Auth.InvalidToken") {
            originalRequest._retry = true;
            return this.api.createSession(this.session.credential.email, this.session.credential.password, (response) => {
              if (response.result != null) {
                this.setSession(this.session.credential, new Token(response.result));
                originalRequest.headers.Authorization = "Bearer " + this.session.token.token;
                return axios(originalRequest);
              }
            });
          } else if (responseData.descriptor === "Auth.InvalidPassword") {
            this.clearSession();
            if (this.listener)
              this.listener.invalidPassword();
          }

          throw error;

        }
        
        
      }
    );
  };

  canBusinessAccess() {
    return this.hasRole(Constants.ROLE_ADMIN_ID);
  }

  canPharmacyAccess() {
    return this.hasRole(Constants.ROLE_PHARMACY_ADMIN_ID);
  }

  canNewsAccess() {
    return this.hasRole(Constants.ROLE_PRESS_ADMIN_ID);
  }

  canNewsHistoryAccess() {
    return this.hasRole(Constants.ROLE_PRESS_ADMIN_ID);
  }

  canCategoriesAcces() {
    return this.hasRole(Constants.ROLE_PRESS_ADMIN_ID);
  }

  canUserAccess() {
    return this.hasRole(Constants.ROLE_ADMIN_ID);
  }

  canEmergencyAccess() {
    return this.hasRole(Constants.ROLE_POLICE_ADMIN_ID);
  }

  canReservationAcces() {
    return this.hasRole(Constants.ROLE_EVENTS_ADMIN_ID);
  }

  canEventsAccess() {
    return this.hasRole(Constants.ROLE_EVENTS_ADMIN_ID);
  }

  canStatsAccess() {
    return this.hasRole(Constants.ROLE_MOBILITY_READER_ID);
  }

  isCitizen() {
    return this.hasRole(Constants.ROLE_CITIZEN_ID) || this.hasRole(Constants.ROLE_VERIFIED_CITIZEN_ID) || this.hasRole(Constants.ROLE_PENDING_VERIFICATION_CITIZEN_ID);
  }

  canAccess() {
    return (
      this.hasRole(Constants.ROLE_ADMIN_ID) ||
      this.hasRole(Constants.ROLE_POLICE_ADMIN_ID) ||
      this.hasRole(Constants.ROLE_PRESS_ADMIN_ID) ||
      this.hasRole(Constants.ROLE_HEALTH_ADMIN_ID) ||
      this.hasRole(Constants.ROLE_EVENTS_ADMIN_ID) ||
      this.hasRole(Constants.ROLE_CITIZEN_ID) ||
      this.hasRole(Constants.ROLE_CALLS_ADMIN_ID) ||
      this.hasRole(Constants.ROLE_PHARMACY_ADMIN_ID)
    );
  }

  hasRole(role) {
    if (this.session) {
      if (this.session.token.role === role) {
        return true;
      }
      if (this.session.token.additionalRoles) {
        if (this.session.token.additionalRoles.includes(role)) {
          return true;
        }
      }
    }
    return false;
  }

}

export default new AuthService(Constants.BASE_URL_SERVER);